import {Injectable} from '@angular/core';
import {Action, select, Store} from '@ngrx/store';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {
  catchError,
  concatMap,
  defaultIfEmpty,
  delay,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  pairwise,
  scan,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  selectUserAccessAdminForStores,
  selectUserAccessStoreAccess,
  selectUserAccessStoreList,
} from '../user-access-store/user-access.selectors';
import {selectIsUserLoggedIn, selectLoginProgressProperty, selectUser} from '../user-store/user.selectors';
import {IStore, isStoreOperationallyDisabled} from '../../../shared/shared-models/store/store';
import {
  path_stores,
  PATH_STORES_AT_STORE_ID,
  path_user_access_userId,
} from '../../../shared/shared-services/database-paths';
import * as UserActions from '../user-store/user.actions';
import * as NotifyActions from '../../notifications/store/notification.actions';
import * as UserAccessActions from '../user-access-store/user-access.actions';
import * as UserStoreActions from './user-store.actions';
import * as StockManagerActions from '../../../../app/features/stock-manager/store/stock-manager.actions';
import {calculatePercentile} from '../../../shared/shared-utils/number/number.utils';
import {UserAccessService} from '../service/user-access-service/user-access.service';
import {FirebaseService} from '../../../shared-utilities/services-old/firebase.service';
import {NavigationEnd, Router} from '@angular/router';
import {Features, IFeature, INavigate, IUserAccess} from '../models';
import {IGlobalError} from '../../../shared/shared-components/components/global-error-display/models/global-error';
import {
  selectAreChangesSaved,
  selectCurrentNavigation,
  selectLocalStoresByFeature,
  selectSelectedFeature,
  selectAllUserStores,
  selectAllUserStoresOrdered,
  selectSelectedStore,
  selectSelectedStores,
  selectSessionStoreIds,
  selectSessionStores,
} from './user-store.selectors';
import {combineLatest, EMPTY, firstValueFrom, forkJoin, from, Observable, of, interval,} from 'rxjs';
import * as NGPReportActions from '../../ngp-report/store/ngp-report.actions';
import {SharedModalUtils} from '../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {ISharedModalBasic} from '../../../shared/shared-models/modals/shared-modal-basic';
import {IconsEnum} from '../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../shared-modules/shared-icons/models/icons-colour.enum';
import * as SettingsActions from '../../settings/store/settings.actions';
import * as SettingActions from '../../settings-new/store/setting.actions';
import {createSharedPaths} from '../../../shared/shared-services/database-paths.shared';
import {NavigationPageEnum} from '../models/navigations.enums';
import {isNonEmptyObject} from '../../../shared/shared-utils/object/object.utils';
import {StoreObject} from '../../../shared-utilities/models-old/store/store-object';
import {doc, docData, FieldValue, Firestore} from '@angular/fire/firestore';
import {LocalStorageService} from '../../core/service/local-storage.service';
import {StoreOperationalStatus} from '../../../shared/shared-models/store/store';
import {getRevokedSessionStoreIds, areSameStringIdSets, getUsableStoreIdsFromUserAccess} from '../utils/access.utils';
import {
  acknowledgeDisabledStoreIds,
  getUnacknowledgedDisabledStores,
} from '../utils/acknowledged-disabled-stores.utils';
import {
  IParsedRouterNavigation,
  isSameNavigationState,
  parseNavigationFromRouterUrl,
  resolveSyncedNavigation,
} from '../utils/navigation-from-router.utils';
import {
  isAutomaticStoreChangeLocked,
  resolveDisplayOrderDefaultStore,
} from '../utils/selected-store-display-order.utils';
import {getLastSeenStoreIds} from '../utils/last-seen-stores.utils';

interface SessionAccessRevocationEmission {
  accessDeleted: boolean;
  revokedIds: string[];
  sessionStores: IStore[];
  userAccess: IUserAccess | null;
}

@Injectable()
export class UserStoresEffects {

  /**
   * Store IDs already accepted for session eviction this login. Prevents duplicate
   * warning modals while reducer state / parallel watcher emissions catch up (FE-95).
   */
  private readonly pendingSessionEvictionIds = new Set<string>();

  /**
   * Authoritative session-store list for serialized eviction. Updated synchronously
   * as each eviction is applied so overlapping watcher actions do not re-read a
   * stale NgRx snapshot and put already-evicted stores back (FE-95).
   */
  private sessionStoresForEviction: IStore[] | null = null;

  /**
   * Display-order default is applied once per login. Later settings reloads
   * (for example a stock-update notification replaying login progress) must not
   * move a store the user is already on (FE-219, FE-248).
   */
  private hasAppliedLoginDisplayOrderDefault = false;

  /**
   * Settings init starts once per login. A later step-5 progress update must not
   * load settings again and re-apply the display-order store (FE-248).
   */
  private hasStartedLoginSettingsInit = false;

  /**
   * Settings finished while Stock Take was open, so the display-order default
   * is postponed until the user leaves that module (FE-248).
   */
  private loginDisplayOrderDefaultDeferred = false;

  //#region GET STORE INFORMATION
  /**
   * ### getStoresInformation$
   * Effect to retrieve store documents based on user access and dispatch associated state updates.
   *
   * This effect listens for the `getStoresInformation` action and queries Firestore for store
   * documents using their IDs. It enhances each store with metadata like sequence, access level,
   * and admin status. Depending on the results, it updates login progress and triggers loading
   * of related notifications.
   *
   * ### Trigger
   * - `UserStoreActions.getStoresInformation`
   *
   * ### Flow
   * 1. Triggered by the `getStoresInformation` action.
   * 2. Queries Firestore for stores using user-accessible store IDs.
   * 3. Enhances each store with admin status, access metadata, and sequence.
   * 4. Dispatches:
   *    - `getStoresInformationSuccess`
   *    - `setLoginProgress`
   *    - `getAllNotificationsForUserStores` (if any stores are returned)
   * 5. If no stores found, adjusts login progress and skips notifications.
   *
   * ### Side Effects
   * - Enhances store objects with computed metadata (admin flag, access, sequence).
   *
   * ### Returns
   * - On success:
   *    - `UserStoreActions.getStoresInformationSuccess`
   *    - `UserActions.setLoginProgress`
   *    - `NotifyActions.getAllNotificationsForUserStores`
   * - On failure:
   *    - `NotifyActions.setGlobalErrorOccurrence`
   *    - `UserAccessActions.setPropertyOnUserAccessState` (for flags and selected store)
   */
  getStoresInformation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.getStoresInformation),
      tap(() => {
        this.pendingSessionEvictionIds.clear();
        this.sessionStoresForEviction = null;
      }),
      withLatestFrom(
        this.store.pipe(select(selectUserAccessStoreList)),
        this.store.pipe(select(selectLoginProgressProperty)),
        this.store.pipe(select(selectUserAccessStoreAccess)),
        this.store.pipe(select(selectUserAccessAdminForStores)),
        this.store.pipe(select(selectUser)),
      ),
      switchMap(([action, storeIdsFromState, loginSteps, storeAccess, adminStores, user]) => {
        // Prefer action.storeIds — already storeList ∩ stores from login (FE-95).
        const storeIds: string[] = action.storeIds?.length > 0
          ? action.storeIds
          : storeIdsFromState.filter((storeId: string): boolean =>
            Object.prototype.hasOwnProperty.call(storeAccess, storeId),
          );

        return this.userAccessService.getMultipleDocumentsByIdsAsObservable<IStore>(
          path_stores(),
          storeIds,
          'storeId',
        ).pipe(
          // Re-check user_access after the store fetch so mid-flight revocations
          // do not briefly enter the session before the live watcher attaches.
          switchMap((loadedStores: IStore[]) => {
            if (!user?.id) {
              return of({
                loadedStores: loadedStores ?? [],
                usableIds: new Set<string>(storeIds),
              });
            }
            return this.userAccessService.getDocumentAsObservable<IUserAccess>(
              path_user_access_userId(user.id),
            ).pipe(
              take(1),
              map((userAccess: IUserAccess | undefined) => {
                const usableIds = new Set<string>(getUsableStoreIdsFromUserAccess(userAccess));
                return {
                  loadedStores: loadedStores ?? [],
                  usableIds,
                };
              }),
              catchError((_error: unknown) => {
                return of({
                  loadedStores: loadedStores ?? [],
                  usableIds: new Set<string>(storeIds),
                });
              }),
            );
          }),
          mergeMap(({
            loadedStores,
            usableIds,
          }: {
            loadedStores: IStore[];
            usableIds: Set<string>;
          }) => {
            const loadedIdSet = new Set<string>(
              loadedStores.map((store: IStore) => store.storeId),
            );
            // Requested IDs omitted by getMultipleDocumentsByIdsAsObservable (missing/errored docs).
            const missingStoreIds: string[] = storeIds.filter(
              (storeId: string): boolean => !loadedIdSet.has(storeId),
            );

            // Exclude DISABLED and no-longer-usable stores from the active session (FE-95).
            const stores: IStore[] = loadedStores.filter(
              (store: IStore) =>
                !this.isStoreDisabled(store) && usableIds.has(store.storeId),
            );
            const activeStoreIds: string[] = stores.map((store: IStore) => store.storeId);

            stores.forEach((store: IStore, idx: number) => {
              store.isAdminForStore = adminStores.includes(store.storeId);
              store.sequence = idx;
              store.userAccess = storeAccess[store.storeId];
            });

            stores.sort((a: IStore, b: IStore) =>
              a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}),
            );
            this.sessionStoresForEviction = stores;

            const missingStoreWarningActions: Action[] = missingStoreIds.length > 0
              ? [
                UserStoreActions.showSessionStoreEvictionWarning({
                  storeNames: missingStoreIds.map(
                    (storeId: string): string => `Unavailable store (${storeId})`,
                  ),
                  storeIds: missingStoreIds,
                  reason: 'MISSING',
                }),
              ]
              : [];

            const disabledLoginNoticeActions: Action[] = [];
            if (action.isLoggingIn && user?.id) {
              const disabledStoresOnLogin: IStore[] = loadedStores.filter(
                (store: IStore) => this.isStoreDisabled(store),
              );
              // Always prune first (even when none are disabled) so re-enabled
              // stores are removed from localStorage on every login.
              const newlyDisabledStores: IStore[] = getUnacknowledgedDisabledStores(
                this.localStorageService,
                user.id,
                disabledStoresOnLogin,
              );

              if (newlyDisabledStores.length > 0) {
                disabledLoginNoticeActions.push(
                  UserStoreActions.queueDisabledStoresLoginNotice({
                    storeNames: newlyDisabledStores.map(
                      (store: IStore) => store.name?.trim() || store.storeId,
                    ),
                    storeIds: newlyDisabledStores.map((store: IStore) => store.storeId),
                  }),
                );
              }
            }

            const nextActions: Action[] = stores.length === 0
              ? [
                UserStoreActions.getStoresInformationSuccess({stores}),
                ...disabledLoginNoticeActions,
                UserActions.setLoginProgress({
                  progress: {
                    ...loginSteps,
                    currentStep: 5,
                    percentComplete: calculatePercentile(5, loginSteps.totalSteps),
                  },
                }),
                ...missingStoreWarningActions,
              ]
              : [];

            if (stores.length === 0) {
              // Login with no usable/active stores still completes (same as historical
              // empty storeList). Logout only happens mid-session when the last
              // remaining active store is removed (evictStoresFromSession$).
              // Queue the disabled notice before step 5 so the waiter is already subscribed.
              return of(nextActions);
            }

            return this.buildFeatureAccessMap(stores).pipe(
              map((accessMap: {[key: string]: IStore[]}) => {
                const actionsWithStores: Action[] = [
                  UserStoreActions.getStoresInformationSuccess({stores}),
                  UserAccessActions.getUserAccessPerFeature({accessMap}),
                  NGPReportActions.getVatRateConversion(),
                  StockManagerActions.getAllTypesenseApiKeysForStores(),
                  SettingsActions.getAllUserSettings(),
                  UserActions.setLoginProgress({
                    progress: {
                      ...loginSteps,
                      currentStep: 4,
                      percentComplete: calculatePercentile(4, loginSteps.totalSteps),
                    },
                  }),
                  NotifyActions.getAllNotificationsLiveChanges({
                    storeIds: activeStoreIds,
                    isLoggingIn: action.isLoggingIn,
                  }),
                  ...disabledLoginNoticeActions,
                  ...missingStoreWarningActions,
                ];
                return actionsWithStores;
              }),
            );
          }),
          mergeMap((actions: Action[]) => actions),
          catchError((error: IGlobalError) =>
            from([
              NotifyActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                },
              }),
              UserAccessActions.setPropertyOnUserAccessState({
                property: 'isStoreInformationLoading',
                value: false,
              }),
              UserAccessActions.setPropertyOnUserAccessState({
                property: 'stores',
                value: undefined,
              }),
              UserAccessActions.setPropertyOnUserAccessState({
                property: 'selectedStore',
                value: undefined,
              }),
            ]),
          ),
        );
      }),
    ),
  );

  /**
   * ### initSettingsOnLoginComplete$
   * Effect to initialize settings after the login process is fully complete.
   *
   * This effect listens for the `setLoginProgress` action and triggers settings
   * initialization when the login reaches the final step (step 5, 100% complete).
   *
   * ### Trigger
   * - `UserActions.setLoginProgress`
   *
   * ### Flow
   * 1. Triggered by the `setLoginProgress` action.
   * 2. Filters for actions where current step equals 5 (final login step).
   * 3. Dispatches `initSettings.start()` to begin settings initialization.
   *
   * ### Side Effects
   * - None
   *
   * ### Returns
   * - When currentStep === 5: `SettingActions.initSettings.start()`
   */
  initSettingsOnLoginComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.setLoginProgress),
      filter((action) => action.progress.currentStep === 5 && !this.hasStartedLoginSettingsInit),
      tap(() => {
        this.hasStartedLoginSettingsInit = true;
      }),
      map(() => SettingActions.initSettings.start()),
    ),
  );

  /**
   * After the first login settings load, select the first store in the header display order
   * when the current store is still the A-Z login default. A store the user already
   * chose is left unchanged. Runs once per login so a later settings reload cannot
   * switch the store (FE-219). While Stock Take is open the switch waits until the
   * user leaves that module (FE-248).
   */
  applyDefaultSelectedStoreFromDisplayOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettingActions.initSettings.complete),
      withLatestFrom(
        this.store.pipe(select(selectAllUserStoresOrdered)),
        this.store.pipe(select(selectAllUserStores)),
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectCurrentNavigation)),
      ),
      mergeMap(([_action, orderedStores, allStores, selectedStore, navigation]) =>
        this.applyLoginDisplayOrderDefault(
          orderedStores,
          allStores,
          selectedStore,
          navigation?.currentPage,
        ),
      ),
    ),
  );

  /**
   * Applies a display-order default that was postponed because settings finished
   * while Stock Take was open. A store the user chose in the meantime stays put.
   */
  applyDeferredDisplayOrderDefaultOnLeave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setNavigationSuccess, UserStoreActions.syncNavigationFromRouter),
      filter(() => this.loginDisplayOrderDefaultDeferred && !this.hasAppliedLoginDisplayOrderDefault),
      filter(({navigation}) => !isAutomaticStoreChangeLocked(navigation?.currentPage)),
      withLatestFrom(
        this.store.pipe(select(selectAllUserStoresOrdered)),
        this.store.pipe(select(selectAllUserStores)),
        this.store.pipe(select(selectSelectedStore)),
      ),
      mergeMap(([action, orderedStores, allStores, selectedStore]) =>
        this.applyLoginDisplayOrderDefault(
          orderedStores,
          allStores,
          selectedStore,
          action.navigation?.currentPage,
        ),
      ),
    ),
  );

  //#endregion

  //#region SET NAVIGATION

  showNavigationLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setNavigation),
      mergeMap(() => [
        UserStoreActions.showNavigationLoading({showLoadingIndicator: true}),
      ]),
    ),
  );

  hideNavigationLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setNavigationSuccess),
      map(() => UserStoreActions.hideNavigationLoadingWithMinDuration()),
    ),
  );

  hideNavigationLoadingWithMinDuration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.hideNavigationLoadingWithMinDuration),
      map(() => {
        return UserStoreActions.showNavigationLoading({showLoadingIndicator: false});
      }),
    ),
  );


  setNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setNavigation),
      withLatestFrom(
        this.store.select(selectAreChangesSaved),
        this.store.select(selectCurrentNavigation).pipe(
          startWith<INavigate | null>(null),
          pairwise(),
          map(([prev, curr]) => prev ?? curr),
        ),
        this.store.select(selectSelectedFeature).pipe(
          startWith<IFeature | null>(null),
          pairwise(),
          map(([prev, curr]) => prev ?? curr),
        ),
      ),
      switchMap(([action, areChangesSaved, prevNavigation, prevFeature]) => {
        const proceed$ = (nav: INavigate, feature: NavigationPageEnum): Observable<({
          navigation: INavigate;
          feature: NavigationPageEnum
        } & Action<'[USER][Navigation] Set User Navigation Success'>) | ({
          selectedFeature: IFeature
        } & Action<'[USER][Selected] Set Selected Feature'>) | Action<'[USER STORES][Last Seen] Update Last Seen'> | ({
          error: IGlobalError
        } & Action<'[NOTIFICATIONS][Error] Set Global Occurrence'>)> =>
          from(this.router.navigate(
            [nav.currentPage],
            {
              queryParams: nav.currentTab ? {tab: nav.currentTab} : {},
            },
          )).pipe(
            mergeMap(() => of(
              UserStoreActions.setNavigationSuccess({navigation: nav, feature}),
              UserStoreActions.setSelectedFeature({selectedFeature: Features[feature]}),
              UserStoreActions.updateLastSeen(),
            )),
            catchError((error: Error) => {
                return of(NotifyActions.setGlobalErrorOccurrence({
                  error: {...error, action: JSON.stringify(action), actionType: action.type, originalError: error},
                }));
              },
            ),
          );
        if (!areChangesSaved) {
          const modalProps: ISharedModalBasic = {
            buttonAccept: true,
            buttonAcceptText: 'Discard Changes & Continue',
            buttonClose: true,
            buttonCloseCross: false,
            buttonCloseText: 'Cancel',
            modalTitle: 'Unsaved Changes',
            modalTitleIcon: IconsEnum.infoSlabCircleOutline,
            modalTitleIconColor: IconColourEnum.yellow,
            contextHeading: 'If you leave this page, your changes will be lost.',
            contextDescription: ['Are you sure you want to continue?'],
            cssClass: ['shared-basic-modal-css-small'],
          };

          if (this.router.url.startsWith('/auth')) {
            return EMPTY;
          }

          return from(this.modalUtils.createModal(modalProps)).pipe(
            switchMap(choice => {
              if (choice?.data?.buttonPressed === 'button-close') {
                return [UserStoreActions.setNavigationSuccess({
                  navigation: {
                    currentPage: prevNavigation.currentPage,
                    currentTab: prevNavigation.currentTab,
                  },
                  feature: prevFeature.feature as NavigationPageEnum,
                  savedChanges: true,
                }),
                  UserStoreActions.setSelectedFeature({
                    selectedFeature: Features[action.feature],
                  }),
                  UserStoreActions.updateLastSeen(),
                ];
              }
              return proceed$(action.navigation, action.feature);
            }),
          );
        }

        return proceed$(action.navigation, action.feature);
      }),
    ),
  );

  /**
   * Keeps header title and sidebar selection aligned with the router URL for
   * browser Back/Forward (and any route change that did not go through setNavigation).
   * Same-feature URLs without an explicit `?tab=` preserve the current shell tab
   * (parent routes like `/stock-take` and nested children like `/stock-take/detail/:id`).
   */
  syncNavigationFromRouter$ = createEffect(() =>
    this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => parseNavigationFromRouterUrl(event.urlAfterRedirects)),
      filter((parsed): parsed is IParsedRouterNavigation => parsed !== null),
      withLatestFrom(
        this.store.pipe(select(selectCurrentNavigation)),
        this.store.pipe(select(selectIsUserLoggedIn)),
      ),
      map(([parsed, currentNavigation, isLoggedIn]: [IParsedRouterNavigation, INavigate, boolean]) => {
        const navigation: INavigate = resolveSyncedNavigation(parsed, currentNavigation);
        return {
          feature: parsed.feature,
          navigation,
          shouldSync: isLoggedIn === true && !isSameNavigationState(currentNavigation, navigation),
        };
      }),
      filter(
        (result: {
          feature: NavigationPageEnum;
          navigation: INavigate;
          shouldSync: boolean;
        }): result is {
          feature: NavigationPageEnum;
          navigation: INavigate;
          shouldSync: true;
        } => result.shouldSync,
      ),
      mergeMap((result: {feature: NavigationPageEnum; navigation: INavigate}) => [
        UserStoreActions.syncNavigationFromRouter({
          navigation: result.navigation,
          feature: result.feature,
        }),
        UserStoreActions.updateLastSeen(),
      ]),
    ),
  );


  //#endregion

  //#region SET SELECTED STORE
  /**
   * ### setSelectedStore$
   * Commits a store switch and refreshes last-seen.
   * Feature pages that the new store cannot use stay open and show a no-access state.
   *
   * ### Trigger
   * - `UserStoreActions.setSelectedStore`
   *
   * ### Returns
   * - `setSelectedStoreSuccess`, `updateLastSeen`
   */
  setSelectedStore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setSelectedStore),
      concatMap(({selectedStore}) => of(
        UserStoreActions.setSelectedStoreSuccess({selectedStore}),
        UserStoreActions.updateLastSeen(),
      )),
    ),
  );


  //#endregion

  //#region UPDATE LAST SEEN
  /**
   * Writes last-seen as soon as the shared multi-store selection changes.
   * Newly selected stores then see the current page without waiting for the 60 second keepalive.
   */
  refreshLastSeenOnMultiStoreSelection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.setSelectedStores, UserStoreActions.setSelectedStoresById),
      map(() => UserStoreActions.updateLastSeen()),
    ),
  );

  /**
   * ### updateLastSeen$
   * Writes the user's last-seen time and current page for each store that should refresh.
   *
   * Dashboard and the stock-manager multi-store tab write every store in the shared header
   * selection. Every other page writes the single active store. GallixService on each store
   * reads `stores/{storeId}/messages/from_app/last_seen` and runs that page's updates while
   * the timestamp is fresh.
   *
   * ### Trigger
   * - `UserStoreActions.updateLastSeen`
   *
   * ### Flow
   * 1. Triggered by `updateLastSeen`.
   * 2. Fetches the current user, stores, navigation, and login state.
   * 3. If the user is logged in and a page is open:
   *    - Merges a timestamp and the current page path onto each target store's last-seen document.
   * 4. On success:
   *    - Returns `EMPTY` (no action dispatched).
   * 5. On error:
   *    - Dispatches `NotifyActions.setGlobalErrorOccurrence`.
   *
   * ### Side Effects
   * - Updates Firestore last-seen documents.
   *
   * ### Returns
   * - On success: `EMPTY`
   * - On failure: `NotifyActions.setGlobalErrorOccurrence`
   */
  updateLastSeen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.updateLastSeen),
      withLatestFrom(
        this.store.pipe(select(selectUser)),
        this.store.pipe(select(selectSelectedStore)),
        this.store.pipe(select(selectSelectedStores)),
        this.store.pipe(select(selectAllUserStoresOrdered)),
        this.store.pipe(select(selectIsUserLoggedIn)),
        this.store.pipe(select(selectCurrentNavigation)),
      ),
      switchMap(([action, user, selectedStore, selectedStores, storesInHeaderOrder, isUserLoggedIn, navigation]) => {
        if (!isUserLoggedIn || !user?.id || !navigation?.currentPage) {
          return EMPTY;
        }
        const storeIds: string[] = getLastSeenStoreIds(
          navigation,
          selectedStore,
          selectedStores,
          storesInHeaderOrder,
        );
        if (storeIds.length === 0) {
          return EMPTY;
        }
        const payload: Record<string, {time: FieldValue; path: string[]}> = {
          [user.id]: {
            time: this.userAccessService.serverTimestamp(),
            path: [navigation.currentPage],
          },
        };
        return forkJoin(
          storeIds.map((storeId: string) =>
            this.userAccessService.updateDocumentAsObservable(
              createSharedPaths().setStoreId(storeId).messagesFromApp(),
              payload,
              true,
            ).pipe(take(1)),
          ),
        ).pipe(
          switchMap(() => EMPTY),
          catchError((error: IGlobalError) =>
            from([
              NotifyActions.setGlobalErrorOccurrence({
                error: {
                  ...error,
                  action: JSON.stringify(action),
                  actionType: action.type,
                  originalError: error,
                },
              }),
            ]),
          ),
        );
      }),
    ),
  );

  //#endregion

  //#region KEEP LAST SEEN ALIVE

  /**
   * ### keepLastSeenAlive$
   * Dispatches `updateLastSeen` every 60 seconds while the user is logged in.
   * This ensures the backend service can detect that the user is still on the same page,
   * not just that they navigated to it once.
   */
  keepLastSeenAlive$ = createEffect(() =>
    interval(60_000).pipe(
      withLatestFrom(this.store.pipe(select(selectIsUserLoggedIn))),
      filter(([, isLoggedIn]) => !!isLoggedIn),
      map(() => UserStoreActions.updateLastSeen()),
    )
  );
  //#region FE-95 SESSION STORE EVICTION

  /**
   * Live `user_access` watcher. Shrink-only: stores removed from `storeList` or the
   * `stores` permission map are evicted from the session without writing Firestore.
   * After a valid access snapshot has been seen, a deleted/unreadable document
   * revokes every remaining session store (FE-95).
   *
   * Starts once per session on the first non-empty store load (`exhaustMap`) so
   * shrink-only `getStoresInformationSuccess` emissions do not reset `seenValidAccess`.
   */
  watchUserAccessForSessionRevocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.getStoresInformationSuccess),
      filter(({stores}: {stores: IStore[]}) => (stores?.length ?? 0) > 0),
      withLatestFrom(this.store.pipe(select(selectUser))),
      exhaustMap(([, user]) => {
        if (!user?.id) {
          return EMPTY;
        }
        const userAccessRef = doc(this.firestore, path_user_access_userId(user.id));
        return docData(userAccessRef).pipe(
          withLatestFrom(this.store.pipe(select(selectSessionStores))),
          scan(
            (
              state: {seenValidAccess: boolean; emission: SessionAccessRevocationEmission | null},
              [rawAccess, sessionStores]: [IUserAccess | undefined, IStore[]],
            ) => {
              const hasValidShape: boolean = !!rawAccess
                && typeof rawAccess === 'object'
                && (Array.isArray(rawAccess.storeList) || !!rawAccess.stores);
              const sessionIds: string[] = (sessionStores ?? []).map((store: IStore) => store.storeId);

              if (hasValidShape) {
                const userAccess: IUserAccess = {
                  ...rawAccess,
                  userId: user.id,
                };
                return {
                  seenValidAccess: true,
                  emission: {
                    userAccess,
                    revokedIds: getRevokedSessionStoreIds(sessionIds, userAccess),
                    sessionStores: sessionStores ?? [],
                    accessDeleted: false,
                  },
                };
              }

              if (state.seenValidAccess && sessionIds.length > 0) {
                return {
                  seenValidAccess: true,
                  emission: {
                    userAccess: null,
                    revokedIds: sessionIds,
                    sessionStores: sessionStores ?? [],
                    accessDeleted: true,
                  },
                };
              }

              return {
                seenValidAccess: state.seenValidAccess,
                emission: null,
              };
            },
            {seenValidAccess: false, emission: null},
          ),
          map((state) => state.emission),
          filter((emission: SessionAccessRevocationEmission | null): emission is SessionAccessRevocationEmission =>
            emission !== null && emission.revokedIds.length > 0,
          ),
          distinctUntilChanged((previous: SessionAccessRevocationEmission, current: SessionAccessRevocationEmission) =>
            areSameStringIdSets(previous.revokedIds, current.revokedIds),
          ),
          mergeMap(({userAccess, revokedIds, sessionStores, accessDeleted}) => {
            const storeNames: string[] = sessionStores
              .filter((store: IStore) => revokedIds.includes(store.storeId))
              .map((store: IStore) => store.name);
            const nextActions: Action[] = [
              UserStoreActions.evictStoresFromSession({
                storeIds: revokedIds,
                storeNames,
                reason: 'REVOKED',
              }),
            ];
            if (accessDeleted) {
              // Drop stale permissions immediately — do not wait for logout modal.
              nextActions.unshift(UserAccessActions.clearUserAccessState());
            } else if (userAccess) {
              nextActions.unshift(UserAccessActions.getUserAccessSuccess({userAccess}));
            }
            return nextActions;
          }),
          takeUntil(this.actions$.pipe(ofType(UserActions.logOutUser))),
        );
      }),
    ),
  );

  /**
   * Live store-document watcher. Evicts session stores whose status becomes DISABLED
   * or whose Firestore document is missing/deleted.
   * Re-enabling a store does not add it back until a full reload.
   *
   * Starts once per session on the first non-empty store load; remaining store IDs
   * are tracked via `selectSessionStoreIds` so shrink-only updates do not tear down
   * the outer subscription.
   */
  watchSessionStoreStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.getStoresInformationSuccess),
      filter(({stores}: {stores: IStore[]}) => (stores?.length ?? 0) > 0),
      exhaustMap(() =>
        this.store.pipe(
          select(selectSessionStoreIds),
          distinctUntilChanged(
            (previous: string[], current: string[]) =>
              previous.length === current.length && previous.every((id: string, index: number) => id === current[index]),
          ),
          switchMap((storeIds: string[]) => {
            if (!storeIds.length) {
              return EMPTY;
            }
            const statusStreams = storeIds.map((storeId: string) =>
              docData(doc(this.firestore, PATH_STORES_AT_STORE_ID(storeId))).pipe(
                map((data: Record<string, unknown> | undefined) => ({
                  storeId,
                  name: (data?.['name'] as string) || storeId,
                  exists: !!data,
                  status: (data?.['status'] as string) || null,
                })),
              ),
            );
            return combineLatest(statusStreams).pipe(
              map((entries) =>
                entries.filter((entry: {storeId: string; name: string; exists: boolean; status: string | null}) =>
                  !entry.exists || this.isStatusDisabled(entry.status),
                ),
              ),
              withLatestFrom(this.store.pipe(select(selectSessionStoreIds))),
              map(([unusableEntries, sessionIds]) =>
                unusableEntries.filter((entry: {storeId: string; name: string}) =>
                  sessionIds.includes(entry.storeId),
                ),
              ),
              filter((unusableEntries) => unusableEntries.length > 0),
              distinctUntilChanged(
                (
                  previous: {storeId: string; name: string}[],
                  current: {storeId: string; name: string}[],
                ) => areSameStringIdSets(
                  previous.map((entry: {storeId: string}) => entry.storeId),
                  current.map((entry: {storeId: string}) => entry.storeId),
                ),
              ),
              map((unusableEntries) =>
                UserStoreActions.evictStoresFromSession({
                  storeIds: unusableEntries.map((entry: {storeId: string; name: string}) => entry.storeId),
                  storeNames: unusableEntries.map((entry: {storeId: string; name: string}) => entry.name),
                  reason: 'DISABLED',
                }),
              ),
            );
          }),
          takeUntil(this.actions$.pipe(ofType(UserActions.logOutUser))),
        ),
      ),
    ),
  );

  /**
   * Applies shrink-only session eviction, refreshes notifications, warns the user,
   * and logs out when no usable stores remain.
   * Evictions are serialized with concatMap and applied against a synchronously
   * updated local session snapshot so overlapping watcher actions cannot restore
   * already-evicted stores from a stale NgRx read (FE-95).
   */
  evictStoresFromSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.evictStoresFromSession),
      concatMap((action) =>
        this.store.pipe(
          select(selectSessionStores),
          take(1),
          withLatestFrom(
            this.store.pipe(select(selectLocalStoresByFeature)),
            this.store.pipe(select(selectLoginProgressProperty)),
            this.store.pipe(select(selectSelectedStore)),
            this.store.pipe(select(selectCurrentNavigation)),
          ),
          mergeMap(([storeSessionStores, accessMap, loginProgress, selectedStore, currentNavigation]) => {
            const sessionStores: IStore[] = this.sessionStoresForEviction ?? storeSessionStores ?? [];
            const sessionIdSet = new Set<string>(
              sessionStores.map((store: IStore) => store.storeId),
            );
            const storeIdsToEvict: string[] = action.storeIds.filter((storeId: string): boolean => {
              if (this.pendingSessionEvictionIds.has(storeId)) {
                return false;
              }
              return sessionIdSet.has(storeId);
            });
            if (storeIdsToEvict.length === 0) {
              return EMPTY;
            }
            storeIdsToEvict.forEach((storeId: string): void => {
              this.pendingSessionEvictionIds.add(storeId);
            });

            const evictedIds = new Set<string>(storeIdsToEvict);
            const storesStillInSession: IStore[] = sessionStores.filter((store: IStore) =>
              evictedIds.has(store.storeId),
            );
            const remainingStores: IStore[] = sessionStores.filter(
              (store: IStore) => !evictedIds.has(store.storeId),
            );
            const remainingIds: string[] = remainingStores.map((store: IStore) => store.storeId);
            this.sessionStoresForEviction = remainingStores;

            const filteredAccessMap: {[key: string]: IStore[]} = {};
            Object.entries(accessMap ?? {}).forEach(([page, pageStores]: [string, IStore[]]) => {
              const filtered: IStore[] = (pageStores ?? []).filter((store: IStore) => !evictedIds.has(store.storeId));
              if (filtered.length > 0) {
                filteredAccessMap[page] = filtered;
              }
            });

            const nameById = new Map<string, string>();
            action.storeIds.forEach((storeId: string, index: number): void => {
              const nameFromAction: string | undefined = action.storeNames?.[index];
              if (nameFromAction) {
                nameById.set(storeId, nameFromAction);
              }
            });
            const storeNames: string[] = storeIdsToEvict.map((storeId: string): string =>
              nameById.get(storeId)
              ?? storesStillInSession.find((store: IStore) => store.storeId === storeId)?.name
              ?? storeId,
            );

            const nextActions: Action[] = [
              UserStoreActions.getStoresInformationSuccess({stores: remainingStores}),
              UserAccessActions.getUserAccessPerFeature({accessMap: filteredAccessMap}),
            ];

            // If the store the user is currently on is removed, leave that feature page
            // for Home so store-scoped UI is not left mounted — warning modal still shows.
            const selectedStoreWasEvicted: boolean =
              !!selectedStore?.storeId && storeIdsToEvict.includes(selectedStore.storeId);
            const alreadyOnHome: boolean =
              currentNavigation?.currentPage === NavigationPageEnum.Home;
            if (selectedStoreWasEvicted && !alreadyOnHome) {
              nextActions.push(
                UserStoreActions.setAreChangesSaved({areChangesSaved: true}),
                UserStoreActions.setNavigation({
                  feature: NavigationPageEnum.Home,
                  navigation: {
                    currentPage: NavigationPageEnum.Home,
                    currentTab: null,
                  },
                }),
              );
            }

            // Preserve login context: mid-login eviction must not cancel the login
            // notification stream with isLoggingIn:false (would leave the user at step 4).
            const isLoginInProgress: boolean = (loginProgress?.percentComplete ?? 100) < 100;

            if (remainingIds.length === 0) {
              nextActions.push(
                // Empty storeIds cancels the prior live notification subscription via switchMap.
                NotifyActions.getAllNotificationsLiveChanges({
                  storeIds: [],
                  isLoggingIn: false,
                }),
                UserStoreActions.showSessionStoreEvictionWarning({
                  storeNames,
                  storeIds: storeIdsToEvict,
                  reason: action.reason,
                  logOutAfterDismiss: true,
                }),
              );
            } else {
              nextActions.push(
                UserStoreActions.showSessionStoreEvictionWarning({
                  storeNames,
                  storeIds: storeIdsToEvict,
                  reason: action.reason,
                }),
                NotifyActions.getAllNotificationsLiveChanges({
                  storeIds: remainingIds,
                  isLoggingIn: isLoginInProgress,
                }),
              );
            }

            return nextActions;
          }),
        ),
      ),
    ),
  );

  clearPendingSessionEvictionsOnLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.logOutUser),
        tap(() => {
          this.pendingSessionEvictionIds.clear();
          this.sessionStoresForEviction = null;
          this.hasAppliedLoginDisplayOrderDefault = false;
          this.hasStartedLoginSettingsInit = false;
          this.loginDisplayOrderDefaultDeferred = false;
        }),
      ),
    {dispatch: false},
  );

  /**
   * Waits until login progress is complete, then shows the queued DISABLED-stores notice.
   * Using the store selector (not only the step-5 action) avoids missing the notice when
   * progress is already 100% or when navigation timing would hide an early modal.
   */
  showQueuedDisabledStoresLoginNotice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.queueDisabledStoresLoginNotice),
      switchMap((action) =>
        this.store.pipe(
          select(selectLoginProgressProperty),
          filter((progress) =>
            (progress?.currentStep ?? 0) >= 5 || (progress?.percentComplete ?? 0) >= 100,
          ),
          take(1),
          delay(750),
          withLatestFrom(this.store.pipe(select(selectIsUserLoggedIn))),
          filter(([, isLoggedIn]: [unknown, boolean]) => isLoggedIn === true),
          map(() =>
            UserStoreActions.showSessionStoreEvictionWarning({
              storeNames: action.storeNames,
              storeIds: action.storeIds,
              reason: 'DISABLED',
              isLoginNotice: true,
            }),
          ),
          takeUntil(this.actions$.pipe(ofType(UserActions.logOutUser))),
        ),
      ),
    ),
  );

  showSessionStoreEvictionWarning$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserStoreActions.showSessionStoreEvictionWarning),
      concatMap(({storeNames, storeIds, reason, logOutAfterDismiss, isLoginNotice}) =>
        from(
          (async (): Promise<Action | null> => {
            const description: string[] = this.buildSessionStoreEvictionDescription(
              storeNames,
              reason,
              logOutAfterDismiss === true,
              isLoginNotice === true,
            );

            await this.modalUtils.createModal({
              modalTitle: reason === 'DISABLED' ? 'Stores Disabled' : 'Store Access Removed',
              modalTitleIcon: IconsEnum.infoSlabCircleOutline,
              modalTitleIconColor: IconColourEnum.yellow,
              contextDescription: description,
              buttonAccept: true,
              buttonAcceptText: logOutAfterDismiss ? 'Log Out' : 'OK',
              buttonClose: false,
              buttonCloseCross: false,
              buttonCloseText: 'Cancel',
              cssClass: ['shared-basic-modal-css-small'],
            });

            if (reason === 'DISABLED' && storeIds?.length) {
              const currentUser = await firstValueFrom(this.store.pipe(select(selectUser), take(1)));
              if (currentUser?.id) {
                acknowledgeDisabledStoreIds(this.localStorageService, currentUser.id, storeIds);
              }
            }

            if (!logOutAfterDismiss) {
              return null;
            }
            return UserActions.logOutUser();
          })(),
        ).pipe(
          filter((action: Action | null): action is Action => action !== null),
        ),
      ),
    ),
  );

  //#endregion

  constructor(
    private actions$: Actions,
    private firebaseService: FirebaseService,
    private firestore: Firestore,
    private router: Router,
    private userAccessService: UserAccessService,
    private modalUtils: SharedModalUtils,
    private readonly store: Store,
    private readonly localStorageService: LocalStorageService,
  ) {
  }

  /**
   * Builds the per-feature store access map only after every page-store lookup resolves.
   * `pageStores` may return EMPTY for pages without rules — those are treated as no access.
   */
  private buildFeatureAccessMap(stores: IStore[]): Observable<{[key: string]: IStore[]}> {
    const pages: NavigationPageEnum[] = Object.values(NavigationPageEnum);
    if (pages.length === 0) {
      return of({});
    }

    return forkJoin(
      pages.map((page: NavigationPageEnum) =>
        this.firebaseService.pageStores(page).pipe(
          take(1),
          map((access: StoreObject) => ({page, access: access})),
          defaultIfEmpty({page, access: undefined as StoreObject | undefined}),
        ),
      ),
    ).pipe(
      map((results: {page: NavigationPageEnum; access: StoreObject | undefined}[]) => {
        const accessMap: {[key: string]: IStore[]} = {};
        results.forEach(({page, access}: {page: NavigationPageEnum; access: StoreObject | undefined}) => {
          if (!access || !isNonEmptyObject(access.stores)) {
            return;
          }
          const pageStores: IStore[] = Object.keys(access.stores)
            .map((id: string) => stores.find((store: IStore) => store.storeId === id))
            .filter((store: IStore): store is IStore => !!store);
          if (pageStores.length > 0) {
            accessMap[page] = pageStores;
          }
        });
        return accessMap;
      }),
    );
  }

  /**
   * Builds the warning modal description for session store eviction / login disable notices.
   */
  private buildSessionStoreEvictionDescription(
    storeNames: string[],
    reason: 'REVOKED' | 'DISABLED' | 'MISSING',
    logOutAfterDismiss: boolean,
    isLoginNotice: boolean,
  ): string[] {
    if (reason === 'DISABLED') {
      const intro: string = isLoginNotice
        ? (storeNames.length === 1
          ? 'The following store is disabled and was not added to your session:'
          : 'The following stores are disabled and were not added to your session:')
        : (storeNames.length === 1
          ? 'The following store was disabled and removed from your session:'
          : 'The following stores were disabled and removed from your session:');
      const description: string[] = [
        intro,
        ...storeNames.map((name: string): string => `• ${name}`),
      ];
      if (logOutAfterDismiss) {
        description.push('You will be logged out because your last active store was removed from this session.');
      }
      return description;
    }

    const namesLabel: string = storeNames.length === 1
      ? `"${storeNames[0]}"`
      : `${storeNames.length} stores`;
    const reasonLabel: string = reason === 'MISSING'
      ? 'could not be loaded'
      : 'is no longer assigned to your account';
    const outcomeLabel: string = reason === 'MISSING'
      ? 'and was not added to your session.'
      : 'and was removed from your session.';
    const description: string[] = [
      `Access to ${namesLabel} ${reasonLabel} ${outcomeLabel}`,
    ];
    if (logOutAfterDismiss) {
      description.push('You will be logged out because your last active store was removed from this session.');
    }
    return description;
  }

  /**
   * Applies the login display-order default once, or postpones it while Stock Take is open.
   * A store other than the A-Z login default is left unchanged.
   */
  private applyLoginDisplayOrderDefault(
    orderedStores: IStore[],
    allStores: IStore[],
    selectedStore: IStore | undefined,
    currentPage: string | null | undefined,
  ): Observable<Action> {
    if (this.hasAppliedLoginDisplayOrderDefault) {
      return EMPTY;
    }
    if (isAutomaticStoreChangeLocked(currentPage)) {
      this.loginDisplayOrderDefaultDeferred = true;
      return EMPTY;
    }
    const firstSelectableStore: IStore | undefined = orderedStores.find(
      (store: IStore) => !isStoreOperationallyDisabled(store),
    );
    const alphabeticalFirstStore: IStore | undefined = [...allStores]
      .filter((store: IStore) => !isStoreOperationallyDisabled(store))
      .sort((a: IStore, b: IStore) => a.name.localeCompare(b.name))[0];
    const nextStore: IStore | null = resolveDisplayOrderDefaultStore(
      selectedStore,
      firstSelectableStore,
      alphabeticalFirstStore,
    );
    if (!firstSelectableStore) {
      return EMPTY;
    }
    this.hasAppliedLoginDisplayOrderDefault = true;
    this.loginDisplayOrderDefaultDeferred = false;
    if (!nextStore) {
      return EMPTY;
    }
    return of(UserStoreActions.setSelectedStore({selectedStore: nextStore}));
  }

  /**
   * True when a store document is operationally DISABLED (case-insensitive).
   */
  private isStoreDisabled(store: IStore): boolean {
    return this.isStatusDisabled(store.status);
  }

  /**
   * True when a raw status string equals DISABLED (case-insensitive).
   */
  private isStatusDisabled(status: string | null | undefined): boolean {
    return String(status ?? '').trim().toUpperCase() === StoreOperationalStatus.Disabled;
  }

}
